import type { BillingStatus } from '../../api/queries/billing'

type Usage = BillingStatus['usage']
/** The currency the backend chose for this visitor; the amounts are already in it. */
export type Currency = BillingStatus['pricing']['currency']

const SYMBOL: Record<Currency, string> = { EUR: '€', USD: '$' }

/** "€24.00", "$3.00" — prices are ex VAT and the backend sends cents. */
export function formatPrice(cents: number, currency: Currency): string {
  return `${SYMBOL[currency]}${(cents / 100).toFixed(2)}`
}

/** "€24/month", "$29/month" for a plan line; whole units only, as on the pricing page. */
export function formatMonthly(cents: number, currency: Currency): string {
  return cents % 100 === 0 ? `${SYMBOL[currency]}${cents / 100}/month` : `${formatPrice(cents, currency)}/month`
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * Every source of analyses the account can draw on, named so the total is
 * checkable against the parts. Each figure is a distinct ledger bucket, so
 * nothing is counted twice (the previous status endpoint returned the one
 * balance under three names).
 */
export function analysesLeft(usage: Usage): { total: number; parts: string[] } {
  const parts = [
    usage.monthly_analyses_remaining > 0 ? `${usage.monthly_analyses_remaining} this month` : null,
    usage.lifetime_free_analyses_remaining > 0 ? `${usage.lifetime_free_analyses_remaining} free` : null,
    usage.purchased_credits_available > 0 ? `${usage.purchased_credits_available} purchased` : null,
    usage.granted_credits_available > 0 ? `${usage.granted_credits_available} granted` : null,
  ].filter((p): p is string => p !== null)
  const total =
    usage.monthly_analyses_remaining +
    usage.lifetime_free_analyses_remaining +
    usage.purchased_credits_available +
    usage.granted_credits_available
  return { total, parts }
}

export function describeAnalysesLeft(usage: Usage): string {
  const { total, parts } = analysesLeft(usage)
  return parts.length > 1 ? `${total} (${parts.join(', ')})` : String(total)
}
